import { IAddressFormat } from "../Profile/AddressField/AddressField";

/**
 * NRI INV foreign (correspondence) address, kept exactly as KRA or the
 * profile GET returned it. Kept apart from GlobalStore/clientPartialDetails_ao
 * so no pincode-master lookup ever rewrites it.
 */
export interface INriForeignAddress {
  pan: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  addressLine4: string;
  City: string;
  State: string;
  PinCode: string;
  Country: string;
  CountryCode: number;
}

const STORAGE_KEY = "nriForeignAddress_ao";

const clean = (value: any): string =>
  value === null || value === undefined || value === "null" || value === "NULL"
    ? ""
    : String(value);

export default class NriAddressStore {
  private static foreignAddress: INriForeignAddress | null = null;

  /** From a KRA response (cache TaggingMode "1" or a live GetKraDetails call). */
  static setFromKra(kraResponse: any, pan?: string) {
    if (!kraResponse) return;
    NriAddressStore.save({
      pan: clean(pan),
      addressLine1: clean(kraResponse.AppCorAdd1),
      addressLine2: clean(kraResponse.AppCorAdd2),
      addressLine3: clean(kraResponse.AppCorAdd3),
      addressLine4: "",
      City: clean(kraResponse.AppCorCity),
      State: clean(kraResponse.AppCorState),
      PinCode: clean(kraResponse.AppCorPincd),
      Country: clean(kraResponse.AppCorCountry),
      CountryCode: 0,
    });
  }

  /** From ObjEcoClientPersoanlDetails.objCorrespondenceAddressDetails of the profile GET. */
  static setFromProfile(corrDetails: any, pan?: string) {
    if (!corrDetails) return;
    NriAddressStore.save({
      pan: clean(pan),
      addressLine1: clean(corrDetails.correspondenceaddressline1),
      addressLine2: clean(corrDetails.correspondenceaddressline2),
      addressLine3: clean(corrDetails.correspondenceaddressline3),
      addressLine4: clean(corrDetails.correspondenceaddressline4),
      City: clean(corrDetails.correspondancecity),
      State: clean(corrDetails.correspondancestate),
      PinCode: clean(corrDetails.correspondancepincode),
      Country: clean(corrDetails.correspondancecountry),
      CountryCode: Number(corrDetails.correspondancecountrycode) || 0,
    });
  }

  /** Cached address for this PAN, or null when nothing (usable) is cached. */
  static get(pan?: string): INriForeignAddress | null {
    if (!NriAddressStore.foreignAddress) {
      try {
        NriAddressStore.foreignAddress = JSON.parse(
          window.localStorage.getItem(STORAGE_KEY) || "null"
        );
      } catch (e) {
        NriAddressStore.foreignAddress = null;
      }
    }
    const cached = NriAddressStore.foreignAddress;
    if (!cached) return null;
    // Never show another application's address
    if (pan && cached.pan && cached.pan.toUpperCase() !== pan.toUpperCase()) return null;
    return cached;
  }

  /** Cached address in the shape ProfileForm/AddressField use. */
  static getAddressObj(pan?: string): IAddressFormat | null {
    const cached = NriAddressStore.get(pan);
    if (!cached) return null;
    return {
      addressLine1: [cached.addressLine1, cached.addressLine2, cached.addressLine3, cached.addressLine4]
        .filter((line) => line)
        .join(" "),
      City: cached.City,
      State: cached.State,
      PinCode: cached.PinCode,
      Country: cached.Country,
      CountryCode: cached.CountryCode,
      // NRI overrides, same as on submit (District=State, Area=City, Statecode=0)
      District: cached.State,
      Area: cached.City,
      StateCode: 0,
    };
  }

  static clear() {
    NriAddressStore.foreignAddress = null;
    window.localStorage.removeItem(STORAGE_KEY);
  }

  private static save(address: INriForeignAddress) {
    const hasData = [
      address.addressLine1,
      address.addressLine2,
      address.addressLine3,
      address.addressLine4,
      address.City,
      address.State,
      address.PinCode,
      address.Country,
    ].some((v) => v.trim() !== "");
    // An empty response (e.g. profile not saved yet) must not wipe a KRA address
    if (!hasData) return;
    NriAddressStore.foreignAddress = address;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(address));
  }
}
